import { Descendant, Transforms } from "slate";
import { ReactEditor } from "slate-react";
import { jsx } from "slate-hyperscript";
import {CustomElement, CustomText} from "../../../types/slate";
import {Deserializer} from "node:v8";

const ELEMENT_TAGS: Record<
  string,
  (el: HTMLElement) => Partial<CustomElement>
> = {
  A: (el) => ({ type: "link", url: el.getAttribute("href") || "" }),
  BLOCKQUOTE: () => ({ type: "block-quote" }),
  H1: () => ({ type: "heading-one" }),
  H2: () => ({ type: "heading-two" }),
  H3: () => ({ type: "heading-three" }),
  UL: () => ({ type: "bulleted-list" }),
  LI: () => ({ type: "list-item" }),
  P: () => ({ type: "paragraph" }),
  CODE: () => ({ type: "code" }),
};

const TEXT_TAGS: Record<string, Partial<CustomText>> = {
  STRONG: { bold: true },
  EM: { italic: true },
  U: { underline: true },
  S: { strikethrough: true },
};

export const deserialize = (el:any): any => {
  if (el.nodeType === 3) {
    return el.textContent
  } else if (el.nodeType !== 1) {
    return null
  } else if (el.nodeName === 'BR') {
    return '\n'
  }

  const { nodeName } = el
  let parent = el

  if (
    nodeName === 'PRE' &&
    el.childNodes[0] &&
    el.childNodes[0].nodeName === 'CODE'
  ) {
    parent = el.childNodes[0]
  }
  let children = Array.from(parent.childNodes).map(deserialize).flat()

  if (children.length === 0) {
    children = [{ text: '' }]
  }

  if (el.nodeName === 'BODY') {
    return jsx('fragment', {}, children)
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el)
    return jsx('element', attrs, children)
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName]
    return children.map(child => jsx('text', attrs, child))
  }

  return children
}
/**
 * A Slate plugin to handle HTML pasting.
 * @param editor - The editor instance.
 * @returns The enhanced editor with HTML paste handling.
 */
export const withPasteHtml = (editor: ReactEditor): ReactEditor => {
  const { insertData, isInline, isVoid } = editor;

  editor.isInline = (element) =>
    element.type === "link" || isInline(element);

  editor.isVoid = (element) =>
    element.type === "image" || isVoid(element);

  editor.insertData = (data) => {
    const html = data.getData('text/html')

    if (html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html')
      const fragment = deserialize(parsed.body)
      Transforms.insertFragment(editor, fragment)
      return
    }

    insertData(data)
  };

  return editor;
};
